import { useState } from 'react';

import { Button, ButtonGroup, Flex } from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunDetailsGate from 'app/components/Reconciliations/ReconciliationRunDetailsGate';
import ReconciliationPagedTable from 'app/components/Reconciliations/ReconciliationPagedTable';

import { defaultPageSize } from 'app/constants/table';

import useFetchSalaryReconciliationCompanies from 'app/hooks/useFetchSalaryReconciliationCompanies';

import { SalaryReconciliationCompanyStatus } from 'app/types/salaryReconciliations';

import getSalaryReconciliationCompanyColumns from 'app/utils/table/salaryReconciliationCompanies';

const STATUS_OPTIONS: { value?: SalaryReconciliationCompanyStatus; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CHECKED', label: 'Checked' },
];

interface SalaryReconciliationCompaniesTabProps {
  reconciliationId: string;
  companyId?: string;
  status?: SalaryReconciliationCompanyStatus;
  onStatusChange: (status?: SalaryReconciliationCompanyStatus) => void;
  isRunning: boolean;
}

/** Lists the companies a salary accrual reconciliation run checked or failed to check, with why a company failed. */
function SalaryReconciliationCompaniesTab({
  reconciliationId,
  companyId = undefined,
  status = undefined,
  onStatusChange,
  isRunning,
}: SalaryReconciliationCompaniesTabProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const { data, isLoading, isError } = useFetchSalaryReconciliationCompanies(
    {
      id: reconciliationId,
      companyId,
      status,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    !isRunning
  );
  const page =
    data && companyId
      ? { ...data, list: data.list.filter((company) => company.companyId === companyId) }
      : data;

  return (
    <ReconciliationRunDetailsGate
      isRunning={isRunning}
      detailsAvailable
      pendingText="Its companies"
    >
      <Flex flexDir="column" gap="20px" w="full">
        <ButtonGroup
          size="sm"
          isAttached
          variant="outline"
          data-testid="salary-reconciliation-companies-status"
        >
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.label}
              isActive={status === option.value}
              onClick={() => {
                onStatusChange(option.value);
                setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
              }}
              data-testid={`salary-reconciliation-companies-status-${option.label.toLowerCase()}`}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>

        <ReconciliationPagedTable
          page={page}
          isLoading={isLoading}
          isError={isError}
          columns={getSalaryReconciliationCompanyColumns(reconciliationId)}
          pagination={pagination}
          onPaginationChange={setPagination}
          getRowId={(company) => company.companyId}
          emptyText={status === 'FAILED' ? 'No company failed in this run' : 'No companies to show'}
          errorText="Sorry, we failed to fetch the companies. Please try again."
          testId="salary-reconciliation-companies"
        />
      </Flex>
    </ReconciliationRunDetailsGate>
  );
}

export default SalaryReconciliationCompaniesTab;
